import React from 'react'
import { connect } from 'react-redux'
import { v4 as uuidV4 } from 'uuid'

import {
  doSwapMergeDirection,
  doSelectTicketsToMerge,
  doFetchMergeableTickets,
  doUpdateMergeSnippetsVisibility,
  doCancel,
  doMerge,
} from 'actions/merge'
import { doOpenTicketMergeTermPage, doOpenTicketMergePage } from 'actions/pages'
import { doFetchOptimisticMergeTickets } from 'actions/tickets'
import { doFetchMergeSearchByQueryString } from 'actions/search'

import { fetchingStatusesSelector } from 'selectors/app'
import {
  selectCurrentMergeSearch,
  selectCurrentMergeSearchQueryString,
  selectCurrentMergeSearchResultTickets,
  selectHasCurrentMergeSearchErrored,
  selectIsCurrentMergeSearchQueryValid,
  selectMergeableForCurrentCustomer,
  selectMergeableTickets,
  selectSnippetTicketIds,
} from 'selectors/search'
import {
  selectMergeSnippetsVisibility,
  selectSnippetsById,
} from 'selectors/tickets'

import {
  selectTargetTicket,
  selectChildTickets,
  selectDirection,
} from 'selectors/merge'
import { selectSendingTicketIds } from 'selectors/undoSend'

import { fixedEncodeURIComponent } from 'util/params'

import MergePage from './view'

const FAKE_MERGE_SECONDS = 3
const emptyObject = {}
const emptyArray = []

const select = state => {
  const mergeableTickets = selectMergeableTickets(state)
  const {
    fetchMergeableTickets: isFetching,
    fetchMergeSearch: isFetchingMoreSearch,
  } = fetchingStatusesSelector(state)
  const isFetchingMore =
    isFetching && mergeableTickets && mergeableTickets.length > 0

  return {
    childTickets: selectChildTickets(state),
    confirmationSeconds: FAKE_MERGE_SECONDS,
    currentMergeSearch: selectCurrentMergeSearch(state) || emptyObject,
    delayedTicketIds: selectSendingTicketIds(state),
    direction: selectDirection(state),
    hasSearchErrored: selectHasCurrentMergeSearchErrored(state),
    isFetching, // true if fetching mergeable or search results
    isFetchingMore, // true if fetching mergeable and fetched some already
    isFetchingMoreSearch, // true if fetching results and fetched some already
    isQueryStringValid: selectIsCurrentMergeSearchQueryValid(state),
    mergeable: selectMergeableForCurrentCustomer(state) || emptyObject,
    mergeableTickets: mergeableTickets || emptyArray,
    queryString: selectCurrentMergeSearchQueryString(state),
    searchResults: selectCurrentMergeSearchResultTickets(state) || emptyArray,
    showSnippets: selectMergeSnippetsVisibility(state),
    snippetsById: selectSnippetsById(state),
    snippetTicketIds: selectSnippetTicketIds(state),
    targetTicket: selectTargetTicket(state),
  }
}

const perform = dispatch => ({
  clearSearch: ticketId => dispatch(doOpenTicketMergePage(ticketId)),
  doFetchNextMergeableTickets: page => dispatch(doFetchMergeableTickets(page)),
  doFetchNextSearchResults: page =>
    dispatch(doFetchMergeSearchByQueryString(page)),
  onCancel: () => dispatch(doCancel()),
  onSearch: ticketId => queryString => {
    if (queryString && queryString.length > 0) {
      const encodedQuery = `${fixedEncodeURIComponent(queryString)}`
      return dispatch(doOpenTicketMergeTermPage(ticketId, encodedQuery))
    }
    return dispatch(doOpenTicketMergePage(ticketId))
  },
  onSelect: ticketId => {
    const ticketIds = [ticketId]
    // Note: the `ticketIds` argument naming here is intentionally ambiguous.
    // We may be fetching a target or a child ticket depending on the merge
    // direction. At this point we dont care which.
    const mergeId = uuidV4()

    // Note: this will change the stored target or child ticket Ids
    dispatch(doSelectTicketsToMerge(ticketIds, mergeId))

    // trigger the optimistic fetch while we are confirming.
    return dispatch(doFetchOptimisticMergeTickets(ticketIds)).then(() => {
      // do the merge after N seconds
      setTimeout(
        id => dispatch(doMerge(id)),
        FAKE_MERGE_SECONDS * 1000,
        // params to pass to doMerge...
        // Note: We cant pass the current target/child
        // ticket props directly as onSelect will have modified them. The
        // doMerge action must select them.
        mergeId
      )
    })
  },
  onSnippetClick: (visible, ticketIds) => {
    dispatch(doUpdateMergeSnippetsVisibility(visible, ticketIds))
  },
  onSwap: ticketId => dispatch(doSwapMergeDirection(ticketId)),
})

const MergeContainer = props => <MergePage {...props} />

export default connect(select, perform)(MergeContainer)
